import { useEffect, useMemo, useRef, useState } from "react";
import { throttle } from "lodash";
import { Autocomplete, Box, Grid, Typography } from "@mui/material";
import Input from "./Input";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import parse from "autosuggest-highlight/parse";

export const GOOGLE_MAPS_API_KEY = "AIzaSyDY7Qf6NI4HYk9Vwmu8c7HtYiVWUD2p1eM";

type Props = {
  className?: string;
  lable?: string;
  value: string;
  disabled?: boolean;
  onChange: (v: string) => void;
  onAddressCommit?: (v: string) => void;
  onFocus?: () => void;
};

function loadScript(src: string, position: HTMLElement | null, id: string) {
  return new Promise<void>((resolve, reject) => {
    if (!position) {
      reject(new Error("Script target not found"));
      return;
    }

    if ((window as any).google?.maps?.places) {
      resolve();
      return;
    }

    const existingScript = document.querySelector(`#${id}`) as HTMLScriptElement | null;
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(), { once: true });
      existingScript.addEventListener("error", reject, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.setAttribute("async", "");
    script.setAttribute("id", id);
    script.src = src;
    script.onload = () => resolve();
    script.onerror = reject;
    position.appendChild(script);
  });
}

const GoogleAddress = ({
  value,
  className,
  lable,
  onChange,
  onAddressCommit,
  onFocus,
  disabled,
}: Props) => {
  const [options, setOptions] = useState<any[]>([]);
  const [isGoogleReady, setIsGoogleReady] = useState(false);
  const autocompleteService = useRef<any>(null);
  const lastCommittedValue = useRef("");
  const currentInputValue = useRef(value || "");

  const commitAddress = (nextValue: string) => {
    const trimmedValue = String(nextValue || "").trim();
    if (trimmedValue === lastCommittedValue.current) return;
    lastCommittedValue.current = trimmedValue;
    onAddressCommit?.(trimmedValue);
  };

  useEffect(() => {
    currentInputValue.current = value || "";
  }, [value]);

  const fetch = useMemo(
    () =>
      throttle((request: any, callback: any) => {
        autocompleteService.current?.getPlacePredictions(request, callback);
      }, 200),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [autocompleteService.current],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    let isMounted = true;

    loadScript(
      `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`,
      document.querySelector("head"),
      "google-maps",
    )
      .then(() => {
        if (!isMounted || autocompleteService.current) return;
        if ((window as any).google?.maps?.places) {
          autocompleteService.current = new (
            window as any
          ).google.maps.places.AutocompleteService();
          setIsGoogleReady(true);
        }
      })
      .catch((error) => {
        console.error("Failed to load Google Maps places script", error);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isGoogleReady || !autocompleteService.current || !value) {
      setOptions([]);
      return;
    }

    fetch({ input: value }, (results: any[]) => {
      setOptions(results || []);
    });
  }, [fetch, isGoogleReady, value]);

  return (
    <Autocomplete
      id="google-map-demo"
      size="small"
      sx={{ width: "100%" }}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option?.description
      }
      filterOptions={(x) => x}
      options={options}
      disabled={disabled}
      autoComplete
      includeInputInList
      filterSelectedOptions
      freeSolo
      clearOnBlur={false}
      className={className}
      value={value}
      inputValue={value || ""}
      noOptionsText="No locations"
      onChange={(event: any, newValue: any, reason: string) => {
        setOptions(newValue ? [newValue, ...options] : options);
        const nextValue = newValue?.description || newValue || "";
        currentInputValue.current = nextValue;
        onChange(nextValue);
        if (reason === "selectOption" || reason === "createOption" || reason === "clear") {
          commitAddress(nextValue);
        }
      }}
      onInputChange={(event: any, newInputValue: string, reason: string) => {
        if (reason === "input" || reason === "clear") {
          currentInputValue.current = newInputValue || "";
          onChange(newInputValue || "");
        }
      }}
      renderInput={(params) =>
        lable ? (
          <Input
            {...params}
            label={lable ? lable : "Address"}
            onFocus={onFocus}
            onBlur={() => commitAddress(currentInputValue.current)}
          />
        ) : (
          <Input
            {...params}
            onFocus={onFocus}
            onBlur={() => commitAddress(currentInputValue.current)}
          />
        )
      }
      renderOption={(props, option) => {
        const matches =
          option.structured_formatting.main_text_matched_substrings || [];

        const parts = parse(
          option.structured_formatting.main_text,
          matches.map((match: any) => [
            match.offset,
            match.offset + match.length,
          ]),
        );

        return (
          <li {...props}>
            <Grid container alignItems="center">
              <Grid item sx={{ display: "flex", width: 44 }}>
                <LocationOnIcon sx={{ color: "text.secondary" }} />
              </Grid>
              <Grid
                item
                sx={{ width: "calc(100% - 44px)", wordWrap: "break-word" }}
              >
                {parts.map((part: any, index: any) => (
                  <Box
                    key={index}
                    component="span"
                    sx={{ fontWeight: part.highlight ? "bold" : "regular" }}
                  >
                    {part.text}
                  </Box>
                ))}
                <Typography variant="body2" color="text.secondary">
                  {option.structured_formatting.secondary_text}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
};

export default GoogleAddress;
